import { Section, SectionHeading, Stagger, StaggerItem } from "@/components/Section";
import { Moon, Flame, Dna, FlaskConical, Droplets, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORIES = [
  {
    id: "sleep",
    name: "Sleep Health",
    tagline: "High-demand, underdiagnosed, recurring revenue potential",
    items: ["Home sleep testing workflows", "CPAP support programs", "Sleep optimization programs", "Remote monitoring coordination"],
    icon: Moon,
    tint: "from-blue-50 to-white",
    dot: "bg-blue-500",
  },
  {
    id: "metabolic",
    name: "Metabolic Health",
    tagline: "Massive consumer demand + subscription structure",
    items: ["Weight management programs", "GLP-1 support (clinic-directed)", "Lab-driven optimization", "Nutrition + coaching add-ons"],
    icon: Flame,
    tint: "from-amber-50 to-white",
    dot: "bg-amber-500",
  },
  {
    id: "hormone",
    name: "Hormone & Longevity",
    tagline: "High-value, premium programs",
    items: ["Hormone optimization workflows", "Preventative lab panels", "Longevity tracking", "Performance optimization"],
    icon: Dna,
    tint: "from-indigo-50 to-white",
    dot: "bg-indigo-500",
  },
  {
    id: "diagnostics",
    name: "Diagnostics & Testing",
    tagline: "Foundational entry point for programs",
    items: ["Specialty labs", "At-home diagnostics", "Biomarker panels", "Screening programs"],
    icon: FlaskConical,
    tint: "from-cyan-50 to-white",
    dot: "bg-cyan-500",
  },
  {
    id: "wellness",
    name: "Wellness & Recovery",
    tagline: "Add-on revenue + bundling opportunities",
    items: ["IV therapy workflows", "Recovery protocols", "Supplementation (clinic-directed)"],
    icon: Droplets,
    tint: "from-emerald-50 to-white",
    dot: "bg-emerald-500",
  },
];

export default function ProgramCategories() {
  return (
    <Section testId="program-categories-section" className="bg-slate-50 border-y border-slate-200">
      <SectionHeading
        eyebrow="Curated Program Catalog"
        title="Curated cash-pay programs built around real market demand."
        intro="Framed as program enablement, workflow infrastructure, and vendor coordination — never treatment or care delivery."
      />

      <Stagger stagger={0.06}>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => (
            <StaggerItem key={c.id}>
              <Link
                to={`/programs#${c.id}`}
                data-testid={`program-card-${c.id}`}
                className={`group relative block h-full rounded-2xl border border-slate-200 bg-gradient-to-br ${c.tint} p-7 hover:shadow-lg hover:border-slate-300 transition-all`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                    <c.icon className="w-5 h-5 text-slate-900" strokeWidth={1.8} />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> Category
                  </div>
                  <h3 className="mt-1.5 font-display text-xl font-semibold text-slate-900">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.tagline}</p>
                </div>

                <ul className="mt-5 space-y-2">
                  {c.items.map((it, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-400 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </Link>
            </StaggerItem>
          ))}
          <StaggerItem>
            <Link
              to="/programs"
              data-testid="program-card-explore-all"
              className="group relative block h-full rounded-2xl border border-dashed border-slate-300 bg-white p-7 hover:border-slate-900 transition-colors flex flex-col justify-between"
            >
              <div>
                <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5" />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-slate-900">Explore the full catalog</h3>
                <p className="mt-1 text-sm text-slate-600">Every program packaged with vendor coordination, intake workflows, and subscription infrastructure.</p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-slate-900">
                View all programs <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </StaggerItem>
        </div>
      </Stagger>
    </Section>
  );
}
